import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { TableCell, TableRow, Tooltip, IconButton, Checkbox } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { formatNumber, getSelectedGeographies, upsertSelectedMetric, getDatapointValue, resetUrlAfterDelete, getDatapointMultiplier } from '../../../helpers';
import SelectableGeography from './SelectableGeography';
import { MetricsTableStyles } from './MetricsTableStyles';
import clsx from 'clsx';
import GeographyPicker from './GeographyPicker';
import Track from '../../../helpers/Analytics';

const MetricRow = ({
  classes,
  metric,
  selectedMetrics,
  onMetricsSelected,
  showGeography,
  allowDeletingMetrics,
  showDatapointCount,
  allYears,
  showDatapoints,
  analyticsPreName,
  disableRowClick,
  openModal
}) => {
  const selectedMetric = selectedMetrics.find(sm => sm.id === metric.id);
  const datapointCount = metric.datapoints ? metric.datapoints.length : metric.datapointCount;
  const noDatapoints = datapointCount < 1;
  const selectedGeographies = getSelectedGeographies(selectedMetric, metric.geographies);
  const numSelectedGeographies = selectedGeographies.filter(sg => sg.selected).length;
  const isSelected = !!(selectedMetric && selectedMetric.selected && (!showGeography || numSelectedGeographies > 0));
  let checkboxTitle;
  if (noDatapoints) {
    checkboxTitle = 'No datapoints';
  } else if (isSelected) {
    if (showDatapoints) {
      checkboxTitle = 'Remove metric from chart only';
    } else {
      checkboxTitle = 'Remove metric from forecast';
    }
  } else {
    if (showDatapoints) {
      checkboxTitle = 'Add metric to chart';
    } else if (disableRowClick === true) {
      checkboxTitle = 'You reached the max amount of selections';
    } else {
      checkboxTitle = 'Add metric to forecast';
    }
  }
  const datapointRows = (showGeography ? selectedGeographies : [{}]).map((geo, i) => {
    const selectedGeoCell = (
      <TableCell
        className={clsx(classes.MetricsTable_MetricGeo, classes.MetricsTable_ChartedMetricDataCell, {
          [classes.MetricsTable_ChartedMetricGeo]: showGeography
        })}
      >
        {selectedGeographies.length === 1 ? (
          <GeographyPicker metric={metric} selectedMetrics={selectedMetrics} onMetricsSelected={onMetricsSelected} />
        ) : (
          <div className={classes.deletable}>
            <SelectableGeography
              metric={metric}
              geo={geo}
              selectedGeographies={selectedGeographies}
              selectedMetrics={selectedMetrics}
              onMetricsSelected={onMetricsSelected}
            />
            <Tooltip title='Remove geography from table and chart'>
              <IconButton
                className={classes.deleteIcon}
                onClick={_ => {
                  Track('Metric', { id: 'Geo-Remove', metric: metric.name, geo: geo.name });
                  onMetricsSelected(
                    upsertSelectedMetric(metric, { geographies: selectedGeographies.filter(sg => sg.id !== geo.id) }, selectedMetrics, true)
                  );
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </TableCell>
    );
    const selectMetric = () => {
      if (!allowDeletingMetrics && isSelected) {
        // if allowDeletingMetrics is false and the user unchecks the metric, remove it from the list of selected metrics
        // we do this because allowDeletingMetrics enables the delete button next to the metric name
        // so if the delete button does not exist, unchecking the metric should "delete" it (remove it from the list of selected metrics)
        onMetricsSelected(selectedMetrics.filter(sm => sm.id !== metric.id));
      } else {
        const allDeselected = selectedGeographies.filter(sg => !sg.selected).length === selectedGeographies.length;
        onMetricsSelected(
          upsertSelectedMetric(
            metric,
            {
              selected: !isSelected,
              geographies: selectedGeographies.map(sg => ({
                selected: isSelected ? false : allDeselected || sg.selected,
                id: sg.id,
                name: sg.name
              }))
            },
            selectedMetrics
          )
        );
      }
    };
    return (
      <TableRow
        key={`${metric.id}_${geo.id}`}
        className={classes.MetricsTable_Row}
        // hover={!showGeography}
        onClick={
          showGeography
            ? null
            : event => {
                if (disableRowClick === true && !isSelected) {
                  openModal(true);
                  return;
                }
                if (noDatapoints) return;
                selectMetric();
                Track(analyticsPreName, { id: `Metric-Toggle`, metric: metric.name, checked: !isSelected });
              }
        }
      >
        {i === 0 ? (
          <>
            <TableCell className={clsx(classes.MetricsTable_IconCell, { [classes.MetricsTable_ChartedIconCell]: showGeography })}>
              <Tooltip enterDelay={300} title={checkboxTitle}>
                <div>
                  <Checkbox
                    disabled={noDatapoints || (disableRowClick === true && !isSelected)}
                    className={classes.MetricsTable_TableIcons}
                    style={{ backgroundColor: 'transparent' }}
                    checked={isSelected}
                    icon={<span className={classes.icon} />}
                    checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
                    onClick={event => {
                      // stop event bubbling, so that the row click handler doesnt get triggered (useful for analytics purposes)
                      event.stopPropagation();
                      event.preventDefault();
                      if (disableRowClick === true && !isSelected) {
                        openModal(true);
                        return;
                      }
                      if (noDatapoints) return;
                      selectMetric();
                      Track(analyticsPreName, { id: `Metric-Toggle`, metric: metric.name, checked: !isSelected });
                    }}
                  />
                </div>
              </Tooltip>
            </TableCell>
            <TableCell
              scope='row'
              className={clsx(classes.MetricsTable_MetricName, {
                [classes.MetricsTable_ChartedMetricName]: showGeography,
                [classes.disabledRow]: disableRowClick
              })}
            >
              {allowDeletingMetrics && selectedMetrics && selectedMetrics.length > 1 ? (
                <div className={classes.deletable}>
                  <Tooltip title={metric.name}>
                    <div className={classes.MetricsTable_MetricNameWrapper}>{metric.name}</div>
                  </Tooltip>
                  <Tooltip title='Remove metric from table and chart'>
                    <IconButton
                      className={classes.deleteIcon}
                      onClick={_ => {
                        Track('Metric', { id: 'Metric-Remove', metric: metric.name });
                        onMetricsSelected(selectedMetrics.filter(sm => sm.id !== metric.id));
                        resetUrlAfterDelete(selectedMetrics.filter(sm => sm.id !== metric.id))
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              ) : allowDeletingMetrics ? (
                <Tooltip title={metric.name}>
                  <div className={classes.MetricsTable_MetricNameWrapper}>{metric.name}</div>
                </Tooltip>
              ) : (
                <>{metric.name}</>
              )}
            </TableCell>
            {showGeography &&
              //  on below table cell
              selectedGeoCell}

            {showDatapointCount && <TableCell>{metric.datapointCount}</TableCell>}
          </>
        ) : (
          <>
            <TableCell
              className={clsx(classes.MetricsTable_IconCell, classes.MetricsTable_ChartedIconCell, classes.MetricsTable_ChartedMetricDataCell)}
            />
            <TableCell
              className={clsx(classes.MetricsTable_MetricName, classes.MetricsTable_ChartedMetricName, classes.MetricsTable_ChartedMetricDataCell)}
            />
            {selectedGeoCell}
          </>
        )}
        {showDatapoints
          ? allYears.map(year => (
              <TableCell key={`${metric.id}_${year}_${geo.id}`} className={classes.MetricsTable_ChartedMetricDataCell}>
                <Tooltip title={getDatapointMultiplier(metric.datapoints, geo.id) || ''} placement='top-start'>
                  <div>
                    {formatNumber(
                      getDatapointValue(metric.datapoints, year, geo.id),
                      metric.datapoints.filter(d => d.geographies.geographyId === geo.id)[0].unit,
                      getDatapointMultiplier(metric.datapoints, geo.id)
                    ) || '- - - - -'}
                  </div>
                </Tooltip>
              </TableCell>
            ))
          : null}
      </TableRow>
    );
  });
  const geographyPickerRow = showGeography && selectedGeographies.length > 1 && (
    <TableRow>
      <TableCell
        className={clsx(classes.MetricsTable_MetricName, classes.MetricsTable_ChartedMetricName, classes.MetricsTable_ChartedMetricEmptyCell)}
      />
      <TableCell className={clsx(classes.MetricsTable_MetricGeo, classes.MetricsTable_ChartedMetricGeo, classes.MetricsTable_ChartedMetricAddButton)}>
        <Tooltip title='Add geographies to table and chart'>
          <div>
            <GeographyPicker metric={metric} selectedMetrics={selectedMetrics} onMetricsSelected={onMetricsSelected} />
          </div>
        </Tooltip>
      </TableCell>
      {allYears.map(year => (
        <TableCell key={year}>
          <div style={{ height: '37px' }}> </div>
        </TableCell>
      ))}
    </TableRow>
  );
  return (
    <React.Fragment>
      {datapointRows}
      {geographyPickerRow}
    </React.Fragment>
  );
};

export default withStyles(MetricsTableStyles)(MetricRow);
